import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react'
import { providersAPI } from '../../services/api'
import { specialtyChips, searchParams } from '../../utils/caseReport'
import { withOriginMiles } from '../../utils/directoryOffice'
import { officeConfirmed, otherOffices, officeMilesText } from '../../utils/officeAddress' // s110
import { zipFirst } from '../../utils/zipFirst' // s110 b77
import { directorySearch } from '../../utils/directorySearch' // PROD-1115
import { OrellaSpinner } from '../OrellaLoader' // s123: the app's one spinner

/**
 * DirectoryNearby — s99 D476 (Dr. Rami 2026-10-10): when the Orella doctor search has no Orella
 * doctor nearby, "the banner for relevant doctors needs to be the selector we built by NPI".
 * The nearest directory doctors (GET /providers/search, the same call the finder makes) for the
 * case's area, Orella subscribers left out (they are already in the list below it). A pick is
 * handed to the caller, which routes it through the share hub (a code first, then the send).
 */
export default function DirectoryNearby({ currentCase, zip, radiusMiles = 25, onPick, onSeeAll, t, variant = 'banner', q = '', onRows, selectedNpi = null, taxonomy = null, origin = null, zipFirstOrder = false, onLoading, sectionTitle = '', sectionNote = '', point = null, onSource = null }) {
  // ★ s123 (Dr. Rami 2026-10-29, "santa rosa is like a dead zone"; PROD 2026-10-30: the ZIP search answered one ZIP):
  //   `point` = where the search is centred (the searched map area, else the patient); sent as lat/lng, the API's near
  //   search measures every office from it and caps OFFICES, not clinicians. The API ignores it until v243_401_0 is
  //   applied (the ZIP search answers, source 'offices'). `onSource` hears the answer's source ('near' = practice names
  //   are searched too).
  // ★ s106 (TestFlight b72, "0 mi" on every row): `origin` is the patient's point (device, searched
  //   address, home ZIP centre or the searched area); each row's miles are worked out on the phone to
  //   its office's street-address point and the rows re-sorted (utils/directoryOffice withOriginMiles).
  // ★ s101 Phase 4 (Dr. Rami 2026-10-13): "searching for a doctor also shows Orella doctors who
  //   one can send to directly" — ONE search on the share hub. variant 'section' is the directory
  //   half of the merged picker: the doctors NOT on Orella inside the picker's distance, matching
  //   its search box, listed under the Orella doctors and drawn on the same map (onRows). The
  //   picker's Filters sheet keeps working: distance and the search box apply here; virtual,
  //   insurance and soonest need Orella data, so they narrow the Orella list only (said in the
  //   section's note). variant 'banner' is the original no-Orella-nearby banner.
  const section = variant === 'section'
  // s101 Build 67: the picker's specialty CHOICE (utils/specialtyChoices) decides the taxonomy, so
  //   the directory and the Orella list always show the same specialty; without it, the case's area.
  const prefixes = useMemo(() => {
    if (Array.isArray(taxonomy)) return taxonomy
    const chips = specialtyChips(currentCase)
    return (chips[0] && chips[0].prefixes) || []
  }, [currentCase?.id, Array.isArray(taxonomy) ? taxonomy.join(',') : '']) // eslint-disable-line react-hooks/exhaustive-deps
  // PROD-1115: `failed` = the search answered no list (5xx, timeout, no network). It is never shown as
  //   "No other doctors found here"; that text is only for a real answer with 0 rows. `attempt` re-runs
  //   the search (Try again).
  const [state, setState] = useState({ loading: true, rows: [], failed: false })
  const [expanded, setExpanded] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const p = searchParams(section ? q : '', zip, prefixes)
    // ★ PRIVACY: the point is rounded to 0.01° (~1 km) before it leaves the phone — never the patient's exact GPS
    //   (utils/zip5Locate's rule: the precise point stays on the phone); a 5-mile search does not need more.
    const pt = point && Number.isFinite(point.lat) && Number.isFinite(point.lng)
      ? { lat: Math.round(point.lat * 100) / 100, lng: Math.round(point.lng * 100) / 100 } : null
    if (!p.zip && !(p.q && p.q.length >= 3)) { setState({ loading: false, rows: [], failed: false }); onRows && onRows([]); onLoading && onLoading(false); return undefined }
    let alive = true
    const ctl = new AbortController()
    setState((st) => ({ ...st, loading: true, failed: false }))
    if (onLoading) onLoading(true) // s114 B2: the map shows its spinner while this search is in flight
    const toRows = (r) => {
      if (onSource && r && r.source) onSource(r.source)
      let near = withOriginMiles((Array.isArray(r?.results) ? r.results : []).filter((x) => !x.onOrella), origin)
      // ★ s114 B3 (Dr. Rami 2026-10-22 build-80 bugs, TestFlight 14: "should give results within the distance
      //   parameter listed"). The API's NAME path is nationwide by design (the MD panel shows it so); on the
      //   app a typed name keeps only the doctors within the chosen distance of the patient's point.
      if (section && p.q && p.q.length >= 3 && origin && Number.isFinite(origin.lat) && Number.isFinite(radiusMiles)) {
        near = near.filter((x) => typeof x.miles === 'number' && x.miles <= radiusMiles)
      }
      // ★ s110 (b77): the Doctors tab lists the selected ZIP first, then beyond (utils/zipFirst).
      return section && zipFirstOrder ? zipFirst(near, zip).rows : near
    }
    const h = setTimeout(() => {
      // ★ s104 (TestFlight b70, "the map only shows clusters"): the section feeds the picker's map, so it
      //   asks for every doctor in range up to 250 (API v243.338.0; an older API clamps to 50).
      // ★ PROD-1115: the fast fallback list shows at once; when it is not the exact list, ONE background
      //   request (exact=1, no spinner) replaces it silently, and a failure of that request keeps it.
      //   An answer from an older search is dropped (alive) and its requests aborted (ctl).
      directorySearch({
        search: providersAPI.search,
        params: { ...p, ...(section && pt ? pt : {}), radiusMiles, limit: section ? 250 : 8 },
        signal: ctl.signal,
        isCurrent: () => alive,
        toRows,
        onRows: (rows) => {
          setState({ loading: false, rows: section ? rows : rows.slice(0, 5), failed: false })
          if (onRows) onRows(rows)
          if (onLoading) onLoading(false)
        },
        onFailed: () => {
          setState({ loading: false, rows: [], failed: true })
          if (onRows) onRows([])
          if (onLoading) onLoading(false)
        },
      })
    }, section ? 350 : 0)
    return () => { alive = false; clearTimeout(h); ctl.abort() }
  }, [zip, radiusMiles, prefixes.join(','), section ? q : '', point ? `${point.lat},${point.lng}` : '', origin ? `${origin.lat},${origin.lng}` : '', attempt]) // eslint-disable-line react-hooks/exhaustive-deps

  const shown = section && !expanded ? state.rows.slice(0, 6) : state.rows
  // s110 (b77, mockup D2): "In 90068" over the rows in the ZIP, "Beyond 90068" over the rest; no headings when
  //   nothing is in the ZIP (the list is then simply by distance).
  const groups = section && zipFirstOrder ? zipFirst(state.rows, zip) : { inZip: 0, zip: '' }
  const headAt = (i) => {
    if (!groups.inZip) return null
    if (i === 0) return { key: 'in', label: t('doctors.inZip', { defaultValue: 'In {{zip}}', zip: groups.zip }), meta: groups.inZip === 1 ? t('doctors.groupCountOne', '1 doctor') : t('doctors.groupCount', { defaultValue: '{{count}} doctors', count: groups.inZip }) }
    if (i === groups.inZip) return { key: 'beyond', label: t('doctors.beyondZip', { defaultValue: 'Beyond {{zip}}', zip: groups.zip }), meta: t('doctors.byDistance', 'by distance') }
    return null
  }
  return (
    <div className="mb-4 flex flex-col gap-2.5" data-testid={section ? 'directory-section' : 'directory-nearby'}>
      {!section && !state.failed && (
        <div className="rounded-xl p-3 flex items-start gap-2 text-[12.5px]" style={{ background: '#F5FBFF', border: '1px solid #A7D9FC', color: '#15354F' }}>
          <AlertTriangle size={14} className="flex-none mt-0.5" style={{ color: '#46A8F9' }} />
          <span>{t('share.noOrellaNearby', { defaultValue: "No doctors connected to Orella within {{n}} miles. Here are doctors near you; we'll send them your Case Report.", n: radiusMiles })}</span>
        </div>
      )}
      {section ? (
        // s123 (board C): the Doctors & Clinicians home names its own heading ("Other offices near you · N", "Nearest
        //   first"); the share step keeps its title and note.
        sectionTitle ? (
          <div className="px-0.5 -mb-0.5 flex items-baseline justify-between gap-2">
            <p className="text-[12px] font-extrabold tracking-wider uppercase" style={{ color: '#475569' }}>{sectionTitle}</p>
            {sectionNote ? <p className="text-[12px] flex-shrink-0" style={{ color: '#64748B' }}>{sectionNote}</p> : null}
          </div>
        ) : (
        <div className="px-0.5 -mb-0.5">
          <p className="text-[15px] font-extrabold" style={{ color: '#15354F' }}>{t('share.otherDoctorsTitle', 'Other doctors near you')}</p>
          <p className="text-[11.5px] leading-snug" style={{ color: '#93A5B4' }}>{t('share.otherDoctorsNote', "These doctors don't currently accept cases directly from Orella. Choose one to send them your case by fax.")}</p>
        </div>
        )
      ) : !state.failed && (
        <p className="text-[10px] font-bold text-gray-400 tracking-wider uppercase -mb-1 px-0.5">
          {t('share.directoryNear', { defaultValue: "Doctors near {{zip}} · we'll fax your report", zip })}
        </p>
      )}
      {state.loading ? (
        <div className="flex justify-center py-4"><OrellaSpinner size={20} /></div>
      ) : state.failed ? (
        // PROD-1115 (Alex Q1/Q3): the search failed. Both the section and the banner say so, with Try again.
        <div className="rounded-xl p-3 flex flex-col items-start gap-2 text-[12.5px]" style={{ background: '#FFF7E8', border: '1px solid #F5D9A8', color: '#15354F' }} data-testid="directory-failed">
          <span className="flex items-start gap-2">
            <AlertTriangle size={14} className="flex-none mt-0.5" style={{ color: '#B45309' }} />
            <span>{t('findDoctor.failed', "We couldn't search right now. Please try again.")}</span>
          </span>
          <button type="button" onClick={() => setAttempt((n) => n + 1)} data-cta="directory-retry" className="text-[13px] font-bold flex items-center gap-1" style={{ color: '#46A8F9' }}>
            <RefreshCw size={12} /> {t('common.tryAgain', 'Try again')}
          </button>
        </div>
      ) : section && state.rows.length === 0 ? (
        <p className="text-[12.5px] px-0.5" style={{ color: '#5A7185' }}>{t('share.otherDoctorsNone', 'No other doctors found here. Try a wider distance or a different search.')}</p>
      ) : shown.map((r, i) => (
        <div key={r.npi} className="flex flex-col gap-2.5">
        {headAt(i) && (
          <div className="flex items-baseline justify-between px-0.5 mt-1" data-testid={`directory-group-${headAt(i).key}`}>
            <span className="text-[11.5px] font-extrabold tracking-wider uppercase" style={{ color: '#15354F' }}>{headAt(i).label}</span>
            <span className="text-[11.5px] font-semibold" style={{ color: '#5A7185' }}>{headAt(i).meta}</span>
          </div>
        )}
        <button type="button" onClick={() => onPick && onPick(r)} data-cta="directory-nearby-row"
                className="flex gap-3 items-center w-full text-left rounded-2xl p-3 bg-white active:scale-[0.99] transition-transform"
                style={{ border: selectedNpi && selectedNpi === r.npi ? '1.5px solid #46A8F9' : '1px solid #E5EDF3' }}>
          <span className="w-[40px] h-[40px] rounded-full grid place-items-center flex-none text-[13px] font-extrabold" style={{ background: '#DCEBF7', color: '#15354F' }}>
            {initials(r.name)}
          </span>
          <span className="flex-1 min-w-0">
            <b className="block text-[14px] font-bold" style={{ color: '#15354F' }}>{r.name}{r.credential ? `, ${r.credential}` : ''}</b>
            <small className="block text-[12px] leading-snug" style={{ color: '#5A7185' }}>
              {[r.specialty, [r.address?.city, r.address?.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
            </small>
            {/* s110: a doctor with several offices is listed at the nearest; the picker asks which one. */}
            {otherOffices(r) > 0 && (
              <small className="block text-[12px] font-bold leading-snug" style={{ color: '#2F7FC9' }} data-testid="directory-offices-count">
                {t('findDoctor.officesCount', { defaultValue: '{{n}} offices · nearest shown', n: otherOffices(r) })}
              </small>
            )}
            <span className="flex flex-wrap gap-1.5 mt-1">
              <span className="inline-block text-[11px] font-bold rounded-full px-2 py-0.5"
                    style={r.faxOnFile ? { color: '#5A7185', background: '#F2F6FA' } : { color: '#B45309', background: '#FFF7E8' }}>
                {r.faxOnFile ? t('findDoctor.faxOnFile', 'Fax on file') : t('findDoctor.noFax', 'No fax listed')}
              </span>
              {/* s110: a home-looking address is never shown; the row says so. */}
              {!officeConfirmed(r) && (
                <span className="inline-block text-[11px] font-bold rounded-full px-2 py-0.5" style={{ color: '#B45309', background: '#FFF7E8' }} data-testid="directory-address-unconfirmed">
                  {t('findDoctor.addressUnconfirmed', 'Address not confirmed')}
                </span>
              )}
            </span>
          </span>
          {typeof r.miles === 'number' && <span className="text-[12px] font-bold flex-none self-start" style={{ color: '#93A5B4' }}>{officeMilesText(r, t)}</span>}
        </button>
        </div>
      ))}
      {section && !expanded && state.rows.length > 6 && (
        <button type="button" onClick={() => setExpanded(true)} data-cta="directory-show-more" className="self-start text-[13px] font-bold flex items-center gap-1" style={{ color: '#46A8F9' }}>
          {t('share.otherDoctorsMore', { defaultValue: 'Show all {{n}}', n: state.rows.length })} <ChevronRight size={14} />
        </button>
      )}
      {onSeeAll && (
        <button type="button" onClick={onSeeAll} className="self-start text-[13px] font-bold flex items-center gap-1" style={{ color: '#46A8F9' }}>
          {t('share.seeAllDoctors', 'See every doctor near you')} <ChevronRight size={14} />
        </button>
      )}
    </div>
  )
}

function initials(name) {
  const parts = String(name || '').replace(/^dr\.?\s+/i, '').split(/\s+/).filter(Boolean)
  return ((parts[0] || '')[0] || '').toUpperCase() + ((parts[parts.length - 1] || '')[0] || '').toUpperCase()
}
